import type { FC } from "react";

import { useHistory } from "react-router";

import {
  Badge,
  type BadgeProps,
  Button,
  Icon,
  Text,
  useModal,
} from "@alpacahq/ui/old";

import {
  useGetEliteAlgoRouting,
  useGetEliteNetDeposits,
  useGetElitePerks,
  useGetIsLegalEntity,
} from "~/v2/api/hooks";
import type { Account } from "~/v2/api/rest/account";
import { Path } from "~/v2/path";
import { cn } from "~/v2/utils/cn";
import { formatAmount } from "~/v2/utils/formatAmount";
import { isPaperOrOnboarding } from "../../../../utils";
import { ALPACA_ELITE_MARKETING_URL } from "../../tradingPlan/constant";

const DEPOSIT_GOAL_30k = 30000;

const DEPOSIT_GOAL_100k = 100000;

export const ELITE_AGREEMENT_TEXT = (
  <Text>
    This Alpaca Elite Agreement (the “Agreement”) sets forth the terms and
    conditions for a customer (“You”) to participate in the Alpaca Elite plan
    (“Alpaca Elite”), as described herein. Alpaca Elite is offered by Alpaca
    Securities LLC (“Alpaca”). Alpaca Elite is a plan through which You can
    receive access to certain premium account features and/or products based on
    certain eligibility criteria. You understand such eligibility criteria is
    subject to change. You understand that Alpaca may at any time and in its
    sole discretion, alter any fees or preferred rates and add or remove
    features from Alpaca Elite. You understand that Alpaca may at any time, in
    its sole discretion and without notice, remove your Alpaca Elite access,
    including if you no longer meet the eligibility criteria for the Alpaca
    Elite plan. You understand that this Agreement is not a recommendation to
    make a specific investment or to use any specific investment strategy. For
    more information on the Alpaca Elite plan, please see{" "}
    <a href={ALPACA_ELITE_MARKETING_URL} className="underline">
      alpaca.markets/elite
    </a>
    .
  </Text>
);

const ProgressBar = ({ percentage }: { percentage: number }) => (
  <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200">
    <div
      className="h-full rounded-full bg-yellow-400"
      style={{ width: `${percentage}%` }}
    ></div>
  </div>
);

const Description = ({
  netDeposit,
  badge,
}: {
  netDeposit: string;
  badge: { variant: BadgeProps["variant"]; text: string };
}) => {
  const modal = useModal({
    title: "Alpaca Elite Agreement",
    content: ELITE_AGREEMENT_TEXT,
  });

  const depositGoal =
    parseFloat(netDeposit ?? "0") > DEPOSIT_GOAL_30k
      ? DEPOSIT_GOAL_100k
      : DEPOSIT_GOAL_30k;

  const percentage = Math.min(
    (parseFloat(netDeposit ?? "0") / depositGoal) * 100,
    100,
  );

  const onClick = () => window.open(ALPACA_ELITE_MARKETING_URL, "_blank");

  return (
    <div className="w-full max-w-96 md:pr-4">
      <div className="flex flex-no-wrap items-start justify-between">
        <Text className="pr-2 font-bold text-base">Alpaca Elite</Text>
        <Badge variant={badge.variant}>{badge.text}</Badge>
      </div>
      <Text className="pt-2 pb-4 text-sm">
        Deposit ${`${formatAmount(depositGoal)}`} or more to unlock Alpaca Elite
        for exclusive perks
      </Text>
      <Text className="pb-1 text-xs">
        Net Deposit: <b>${formatAmount(netDeposit)}</b> / $
        {formatAmount(depositGoal)}
      </Text>
      <ProgressBar percentage={percentage} />
      <Button
        variant="alternate"
        className="mt-4 w-full justify-self-end border-2 border-yellow-400"
        onClick={onClick}
      >
        Discover More about Alpaca Elite
      </Button>
      <button
        type="button"
        onClick={modal.toggle}
        className="pt-2 text-xs underline"
      >
        Terms and Conditions
      </button>
      {modal.component}
    </div>
  );
};

const CheckIcon: FC<{ isChecked: boolean }> = ({ isChecked }) => (
  <Icon
    name={isChecked ? "CheckCircle" : "LockClosed"}
    variant={isChecked ? "filled" : "line"}
    className={cn(isChecked ? "text-green-500" : "text-black")}
  />
);

const ThirtyK = ({ plan, isChecked }: { plan: string; isChecked: boolean }) => {
  const history = useHistory();

  return (
    <div className="w-full max-w-96 lg:pr-4">
      <Text className="pb-2 font-bold text-base">$30k Deposit to unlock</Text>
      <div className="flex min-h-40 flex-col justify-between rounded border border-gray-200 bg-gray-100 p-4">
        <div className="flex items-center justify-between pb-4">
          <Text className="font-bold text-base">New Trading Plan</Text>
          <CheckIcon isChecked={isChecked} />
        </div>
        <Text className="pb-4 text-sm">Current Plan: {plan}</Text>
        <Button
          variant="alternate"
          className="w-full justify-self-end border-2 border-yellow-400"
          onClick={() => history.push(Path.ROUTE_ALPACA_TRADING_PLAN)}
        >
          Change Trading Plan
        </Button>
      </div>
    </div>
  );
};

const HundredK = ({ isChecked }: { isChecked: boolean }) => {
  const { isLegalEntity, isLegalEntityLoading } = useGetIsLegalEntity();

  return (
    <div className="w-full max-w-96">
      <Text className="pb-2 font-bold text-base">$100k Deposit to unlock</Text>
      <div className="flex min-h-40 flex-col justify-between rounded border border-gray-200 bg-gray-100 p-4">
        <div className="flex items-center justify-between">
          <Text className="text-base">Lower Margin Rate</Text>
          <CheckIcon isChecked={isChecked} />
        </div>
        {!isLegalEntity && !isLegalEntityLoading && (
          <div className="flex items-center justify-between">
            <Text className="text-base">Free Market Data Subscription</Text>
            <CheckIcon isChecked={isChecked} />
          </div>
        )}
        <div className="flex items-center justify-between">
          <Text className="text-base">White-Glove Support</Text>
          <CheckIcon isChecked={isChecked} />
        </div>
      </div>
    </div>
  );
};

export const EliteCard = ({ account }: { account?: Account }) => {
  const { netDeposit } = useGetEliteNetDeposits({
    accountID: account?.id || "",
    options: { enabled: !!account?.id },
  });

  const { model } = useGetEliteAlgoRouting({
    accountID: account?.id || "",
    options: {
      enabled: !!account?.id,
    },
  });

  const { perks } = useGetElitePerks({
    accountID: account?.id || "",
    options: {
      enabled: !!account?.id && !isPaperOrOnboarding(account),
    },
  });

  const plan =
    model === "cost_plus" || model === "all_in"
      ? "Elite Smart Router"
      : "Commission-Free";

  const badge: { variant: BadgeProps["variant"]; text: string } =
    perks?.elite_status
      ? { variant: "blue", text: `Elite ${perks.elite_status}` }
      : { variant: "gray", text: "Not Enabled" };

  return (
    <div className="mb-4 grid w-full max-w-fit grid-cols-1 gap-4 rounded border border-gray-200 bg-white p-6 md:grid-cols-2 lg:grid-cols-3">
      <Description netDeposit={netDeposit ?? "0"} badge={badge} />
      <ThirtyK
        plan={plan}
        isChecked={
          perks?.elite_status === "30k" || perks?.elite_status === "100k"
        }
      />
      <HundredK isChecked={perks?.elite_status === "100k"} />
    </div>
  );
};
